import React, { useEffect, useState } from 'react'
import { gql, useMutation, useQuery } from '@apollo/client'
import { Box, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, Typography } from '@mui/material'
import { GET_UPLOAD_STATUS } from '../../apollo/advancedMenuQueries'
import FeeFields, { FEE_QUERY_FIELDS, FeeCompareTable, feeErrors, feesToForm, formToFees } from './FeeFields'
import { REVIEW } from './FeeApprovals'
import { Icon, T } from './onboardingDesign'
import { Badge, Card, CardTitle, FieldLabel, GhostButton, KV, LinkButton, Notice, OutlineButton, PrimaryButton } from './onboardingUi'

/**
 * The admin side of one merchant application from /partner-signup
 * (migration 122), next to the form itself on its page
 * (views/PartnerApplication.jsx): what to do next, approve & onboard, the
 * fees, the agreement, the KYB documents and the menu. The answers are in the
 * form; here secrets are masked to their last 4 by the server.
 */

const SIGNUP_FIELDS = `
  _id status isApplication currentStep legalName agreementVersion signerName signerTitle signerEmail signerIp
  signedAt signedPdfSha256 templateSha256 countersignerName countersignerTitle countersignedAt executedPdfSha256
  cardBrand cardLast4 cardExp restaurantId vendorId approvedAt onboardingLog businessType agentName
`

const GET_APPLICATION = gql`
  query PartnerSignupApplication($id: ID!) {
    partnerSignupApplication(id: $id) {
      pricing {
        status held requestId requestStatus requestNote startedByName inviteSentAt sendRequested
        fees { ${FEE_QUERY_FIELDS} }
        standard { ${FEE_QUERY_FIELDS} }
      }
      signup { ${SIGNUP_FIELDS} }
      application
      consents
      checklist
      missing
      documents { _id kind ownerIndex name mimeType size uploadedAt }
      existingRestaurant { restaurantId name address ownerName ownerEmail hasStripe hasQor how }
      attachDecision
    }
  }
`
const SET_RESTAURANT = gql`
  mutation SetApplicationRestaurant($id: ID!, $restaurantId: ID, $createNew: Boolean) {
    setApplicationRestaurant(id: $id, restaurantId: $restaurantId, createNew: $createNew) { restaurantId how }
  }
`
const RESTAURANT_CHOICES = gql`query ApplicationRestaurantChoices { currentMerchants { restaurantId name address } }`
const APPROVE = gql`mutation ApprovePartnerSignup($id: ID!) { approvePartnerSignup(id: $id) { ${SIGNUP_FIELDS} } }`
const RESEND_STRIPE = gql`mutation ResendPartnerStripeSetup($id: ID!) { resendPartnerStripeSetup(id: $id) }`
const RESEND_LINK = gql`mutation ResendPartnerApplicationLink($id: ID!) { resendPartnerApplicationLink(id: $id) }`
const SET_FEES = gql`
  mutation SetMerchantApplicationFees($id: ID!, $fees: MerchantAgreementFeesInput) {
    setMerchantApplicationFees(id: $id, fees: $fees) { status }
  }
`

const IMPORT_MENU = gql`mutation ImportPartnerSignupMenu($id: ID!) { importPartnerSignupMenu(id: $id) { menuId uploadIds } }`

const AGENT_LINK_FIELDS = 'agentId agentName rate since endedAt thisMonth { orders subtotal amount }'
const AGENT_LINK = gql`query AgentRestaurantLink($id: ID!) { agentRestaurantLink(partnerSignupId: $id) { ${AGENT_LINK_FIELDS} } }`
const STOP_AGENT = gql`mutation StopAgentRestaurantEarnings($id: ID!) { stopAgentRestaurantEarnings(partnerSignupId: $id) { ${AGENT_LINK_FIELDS} } }`

const money = (v) => `$${(Number(v) || 0).toFixed(2)}`
const shortDay = (v) => (v ? new Date(v).toLocaleDateString() : '—')

/**
 * The agent who brought this restaurant in earns a % of every online order's
 * subtotal (backend utils/agentEarnings.js), set when it was approved. A
 * super admin can stop that from now on. Shows nothing without an agent.
 */
function AgentEarningsCard({ id, version }) {
  const { data, refetch } = useQuery(AGENT_LINK, { variables: { id }, fetchPolicy: 'network-only' })
  const [stop, { loading: stopping }] = useMutation(STOP_AGENT)
  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState(null)
  useEffect(() => {
    if (version) refetch().catch(() => {})
  }, [version, refetch])
  const l = data && data.agentRestaurantLink
  if (!l) return null
  const m = l.thisMonth
  const doStop = async () => {
    setError(null)
    try {
      await stop({ variables: { id } })
      setConfirming(false)
      await refetch()
    } catch (e) {
      setError(e.message)
    }
  }
  return (
    <Card>
      <CardTitle action={<Badge kind={l.endedAt ? 'neutral' : 'green'}>{l.endedAt ? 'Stopped' : 'Earning'}</Badge>}>
        Agent commission on orders
      </CardTitle>
      {error && <Notice severity="error">{error}</Notice>}
      <Typography sx={{ fontSize: 14, color: T.text2, mb: 1.5 }}>
        {l.agentName || 'The agent'} earns <strong>{Number(l.rate)}%</strong> of the subtotal of every online order from this restaurant
        {l.endedAt ? `, from ${shortDay(l.since)} until ${shortDay(l.endedAt)}.` : `, since ${shortDay(l.since)}.`}
      </Typography>
      <KV columns={1} rows={[
        ['This month so far', m ? `${money(m.amount)} on ${m.orders} order${m.orders === 1 ? '' : 's'} (${money(m.subtotal)} subtotal)` : '—'],
      ]} />
      {!l.endedAt && (
        confirming ? (
          <Box sx={{ mt: 1.5, p: 1.5, borderRadius: '12px', background: T.footerBg, border: `1px solid ${T.cardBorder}` }}>
            <Typography sx={{ fontSize: 13.5, color: T.text2, mb: 1 }}>
              Stop {l.agentName || 'the agent'} earning on this restaurant’s orders from now on? What they already earned stays.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <PrimaryButton busy={stopping} onClick={doStop}>Stop earning</PrimaryButton>
              <GhostButton onClick={() => setConfirming(false)}>Cancel</GhostButton>
            </Box>
          </Box>
        ) : (
          <LinkButton onClick={() => setConfirming(true)} sx={{ mt: 1, ml: -1 }}>Stop the agent earning on this restaurant</LinkButton>
        )
      )}
    </Card>
  )
}

/** One AI menu import's progress, polled until it finishes. */
function MenuImportProgress({ uploadId }) {
  const { data, stopPolling } = useQuery(GET_UPLOAD_STATUS, { variables: { uploadId }, pollInterval: 3000, fetchPolicy: 'network-only' })
  const u = data && data.advancedMenuUploadStatus
  const done = !!u && ['COMPLETED', 'FAILED'].includes(u.aiProcessingStatus)
  useEffect(() => {
    if (done) stopPolling()
  }, [done, stopPolling])
  const icon = !u || !done ? <CircularProgress size={14} sx={{ color: T.red }} />
    : u.aiProcessingStatus === 'COMPLETED' ? <Box sx={{ color: T.green, display: 'flex' }}><Icon.Check size={18} /></Box> : <Box sx={{ color: T.error, display: 'flex' }}><Icon.Alert size={18} /></Box>
  const text = !u ? 'Waiting to start…'
    : u.aiProcessingStatus === 'COMPLETED' ? `Imported ${u.itemsImported || 0} items${u.itemsFailed ? `, ${u.itemsFailed} could not be added` : ''}.`
      : u.aiProcessingStatus === 'FAILED' ? `Failed: ${u.progressMessage || 'the AI could not read this file.'}`
        : u.progressMessage || 'Reading the menu…'
  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', py: 0.5 }}>
      {icon}
      <Typography sx={{ fontSize: 14, color: u && u.aiProcessingStatus === 'FAILED' ? T.error : T.text2 }}>{text}</Typography>
    </Box>
  )
}

const kb = (size) => `${Math.max(1, Math.round(size / 1024))} KB`

/** A file row: name, size, Download. */
function FileRow({ title, name, size, onDownload }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 1, borderTop: `1px solid ${T.divider}`, '&:first-of-type': { borderTop: 0 } }}>
      <Box sx={{ width: 36, height: 36, borderRadius: '10px', background: T.redTint, color: T.redText, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon.FileText size={18} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        {title && <Typography sx={{ fontWeight: 600, fontSize: 14, color: T.text }}>{title}</Typography>}
        <Typography sx={{ fontSize: 13, color: T.hint, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {name}{size != null ? ` · ${kb(size)}` : ''}
        </Typography>
      </Box>
      <LinkButton onClick={onDownload} aria-label={`Download ${name}`} sx={{ minWidth: 0, px: 1.25 }}>
        <Icon.Download size={18} />
      </LinkButton>
    </Box>
  )
}

/**
 * The menu the restaurant uploaded with its application. Once approved (the
 * restaurant exists), one click imports it into the restaurant's menu with
 * the AI menu import (backend utils/signupMenuImport.js).
 */
function MenuSection({ id, docs, restaurantId, log, run, download }) {
  const [importMenu, { loading }] = useMutation(IMPORT_MENU)
  const imp = log.menuImport
  return (
    <Card>
      <CardTitle sub={docs.length ? null : 'No menu uploaded.'}>Menu ({docs.length})</CardTitle>
      {docs.length > 0 && (
        <>
          <Box sx={{ mb: 1.5 }}>
            {docs.map((d) => <FileRow key={d._id} name={d.name} size={d.size} onDownload={() => download(d)} />)}
          </Box>
          {!restaurantId ? (
            <Typography sx={{ color: T.hint, fontSize: 13.5 }}>
              Approve the application, then import this menu into the restaurant with AI from here.
            </Typography>
          ) : imp ? (
            <Box>
              <Typography sx={{ color: T.hint, fontSize: 13.5, mb: 0.5 }}>
                Imported into the restaurant’s “Menu”{imp.by ? ` by ${imp.by}` : ''} on {fmtDate(imp.at)}. Review the items on the restaurant’s Menus page; it stays hidden from customers until it’s set up.
              </Typography>
              {(imp.uploadIds || []).map((u) => <MenuImportProgress key={u} uploadId={u} />)}
            </Box>
          ) : (
            <PrimaryButton
              busy={loading}
              onClick={() => run(() => importMenu({ variables: { id } }), 'Menu import started. The AI is reading the files; this usually takes a minute or two.')}
            >
              <Icon.Sparkle size={16} />
              Import menu with AI
            </PrimaryButton>
          )}
        </>
      )}
    </Card>
  )
}

const PRICING_LABEL = {
  STANDARD: ['Standard fees', 'neutral'],
  CUSTOM: ['Own fees', 'amber'],
  PENDING: ['Agent’s fees waiting for approval', 'amber'],
  REJECTED: ['Agent’s fees not approved', 'red'],
  SIGNED: ['Signed', 'purple'],
}

/**
 * This application's Schedule A fees. A super admin can change them until it
 * is signed (backend utils/merchantInvites.js), and decide an agent's
 * request. Approving or setting fees on an application that hasn't been sent
 * emails the merchant the link.
 */
function PricingSection({ id, pricing: p, signupStatus, run }) {
  const [setFees, { loading: saving }] = useMutation(SET_FEES)
  const [review, { loading: reviewing }] = useMutation(REVIEW)
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState(null)
  const [rejectNote, setRejectNote] = useState('')
  const [rejecting, setRejecting] = useState(false)
  const label = PRICING_LABEL[p.status] || PRICING_LABEL.STANDARD
  const open = signupStatus === 'DRAFT'
  const fees = p.fees || p.standard
  // Held and already sent for signature: a decision on the fees sends it.
  const sends = p.held && p.sendRequested

  const save = async (value) => {
    await run(() => setFees({ variables: { id, fees: value } }), sends
      ? 'Fees saved, and the merchant was emailed the link.'
      : 'Fees saved. The merchant sees them on the Fees & terms step.', { fees: true })
    setEditing(false)
  }

  return (
    <Card>
      <CardTitle action={<Badge kind={label[1]}>{label[0]}</Badge>}>Pricing</CardTitle>
      <Typography sx={{ color: T.text2, fontSize: 14, mb: 1.5 }}>
        {p.startedByName ? `Started by ${p.startedByName}. ` : ''}
        {p.held
          ? (p.sendRequested ? 'Sent for signature, held until the fees are approved.' : 'Still being filled in; not sent to the merchant yet.')
          : p.inviteSentAt ? `Link emailed ${fmtDate(p.inviteSentAt)}.` : ''}
      </Typography>
      {p.requestStatus === 'REJECTED' && p.requestNote && (
        <Typography sx={{ color: T.error, fontSize: 14, mb: 1 }}>Rejected: {p.requestNote}</Typography>
      )}
      {p.status !== 'STANDARD' && <FeeCompareTable fees={fees} standard={p.standard} label={p.status === 'PENDING' ? 'Proposed' : 'This restaurant'} />}
      {open && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
          {p.status === 'PENDING' && (
            <>
              <PrimaryButton
                tone="green" disabled={reviewing}
                onClick={() => run(() => review({ variables: { id: p.requestId, approve: true } }), sends
                  ? 'Approved. The merchant was emailed the link and the agent was told.'
                  : 'Approved. The agent was told; it goes to the merchant when it is sent for signature.', { fees: true })}
              >
                <Icon.Check size={16} />
                {sends ? 'Approve and send' : 'Approve fees'}
              </PrimaryButton>
              <OutlineButton onClick={() => setRejecting(x => !x)}>Reject</OutlineButton>
            </>
          )}
          <GhostButton onClick={() => { setForm(feesToForm(fees)); setEditing(true) }}>
            <Icon.Pen size={16} />
            {p.status === 'STANDARD' ? 'Set own fees' : 'Edit fees'}
          </GhostButton>
          {p.status === 'CUSTOM' && (
            <LinkButton disabled={saving} onClick={() => save(null)}>Use standard fees</LinkButton>
          )}
        </Box>
      )}
      {rejecting && (
        <Box sx={{ mt: 2 }}>
          <FieldLabel htmlFor="pricing-reject-note" label="Why (sent to the agent)" optional />
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <TextField id="pricing-reject-note" size="small" value={rejectNote} onChange={e => setRejectNote(e.target.value)} sx={{ flex: 1, minWidth: 200 }} inputProps={{ maxLength: 500 }} />
            <PrimaryButton
              disabled={reviewing}
              onClick={() => run(() => review({ variables: { id: p.requestId, approve: false, note: rejectNote.trim() || null } }), 'Not approved. The agent was told.', { fees: true })}
            >
              Reject fees
            </PrimaryButton>
          </Box>
        </Box>
      )}
      <Dialog open={editing} onClose={() => setEditing(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Fees for this restaurant</DialogTitle>
        <DialogContent dividers sx={{ borderColor: T.divider }}>
          <Typography sx={{ color: T.text2, fontSize: 14, mb: 2 }}>
            They apply right away{p.status === 'PENDING' ? ', in place of the agent’s proposal' : ''}.
            {sends ? ' It was sent for signature, so the merchant is emailed the link when you save.' : ''}
          </Typography>
          {form && <FeeFields value={form} onChange={setForm} standard={p.standard} />}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <GhostButton onClick={() => setEditing(false)}>Cancel</GhostButton>
          <PrimaryButton
            busy={saving} disabled={!form || Object.keys(feeErrors(form)).length > 0}
            onClick={() => save(formToFees(form))}
          >
            {sends ? 'Save and send' : 'Save fees'}
          </PrimaryButton>
        </DialogActions>
      </Dialog>
    </Card>
  )
}

const KIND_LABELS = {
  VOIDED_CHECK: 'Voided check / bank letter',
  OWNER_ID_FRONT: 'Photo ID (front)',
  OWNER_ID_BACK: 'Photo ID (back)',
  BUSINESS_LICENSE: 'Business license',
  PROCESSING_STATEMENT: 'Processing statement',
  EIN_LETTER: 'IRS EIN letter',
  FOOD_PERMIT: 'Food / health permit',
  MENU: 'Menu',
  OTHER: 'Other',
  SIGNED_AGREEMENT: 'Signed agreement (merchant)',
  EXECUTED_AGREEMENT: 'Executed agreement (countersigned)',
}
const STEP_ORDER = [
  ['vendor', 'Dashboard login (vendor)'],
  ['restaurant', 'Restaurant created (hidden until set up)'],
  ['countersign', 'Agreement countersigned'],
  ['stripe', 'Stripe connected account'],
  ['qor', 'QorCommerce boarding'],
  ['email', 'Approval email to the owner'],
  ['agentCommission', 'Agent commission'],
  ['agentOrders', 'Agent commission on orders'],
]

function parse(json, fallback) {
  try { return json ? JSON.parse(json) : fallback } catch (e) { return fallback }
}

function fmtDate(v) {
  if (!v) return '—'
  const d = new Date(v)
  return isNaN(d.getTime()) ? '—' : d.toLocaleString()
}

function authToken() {
  try {
    const vendor = localStorage.getItem('user-enatega')
    if (vendor) return JSON.parse(vendor).token
  } catch (e) { /* not logged in */ }
  return null
}

async function downloadDocument(doc) {
  const base = (process.env.REACT_APP_SERVER_URL || 'http://localhost:8001').replace(/\/+$/, '')
  const res = await fetch(`${base}/admin/merchant-onboarding/documents/${doc._id}`, {
    headers: { authorization: `Bearer ${authToken()}` },
  })
  if (!res.ok) throw new Error(res.status === 403 ? 'Only admins can download documents.' : 'Download failed.')
  const url = URL.createObjectURL(await res.blob())
  const a = document.createElement('a')
  a.href = url
  a.download = doc.name
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 30000)
}

/** The steps Approve & onboard runs, each with how it went. */
function OnboardSteps({ log, agentName }) {
  const attached = !!(log.restaurant && log.restaurant.attached)
  // No agent commission for a merchant already on RushMyFood.
  return STEP_ORDER.filter(([key]) => !key.startsWith('agent') || (agentName && !attached)).map(([key, label]) => {
    const st = log[key]
    const icon = !st ? <Box sx={{ color: T.inputHover, display: 'flex' }}><Icon.Circle size={18} /></Box>
      : st.ok ? <Box sx={{ width: 20, height: 20, borderRadius: '50%', background: T.green, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon.Check size={13} /></Box>
        : <Box sx={{ color: T.error, display: 'flex' }}><Icon.Alert size={20} /></Box>
    const detail = !st ? '' : !st.ok ? st.error
      : key === 'vendor' ? `Vendor #${st.vendorId}${st.created ? ' (new, welcome email sent)' : st.owner ? ' (the restaurant’s owner)' : ' (existing login)'}`
        : key === 'restaurant' ? (st.attached
          ? `Existing restaurant #${st.restaurantId}, attached. Commission now ${st.commission.deliveryCommissionRate}/${st.commission.selfDeliveryCommissionRate}/${st.commission.pickupCommissionRate}% (delivery/self/pickup)${st.previousCommission ? `, was ${[st.previousCommission.deliveryCommissionRate, st.previousCommission.selfDeliveryCommissionRate, st.previousCommission.pickupCommissionRate].map(v => (v == null ? 'default' : v)).join('/')}` : ''}`
          : `Restaurant #${st.restaurantId}${st.geocoded ? '' : ' (address not geocoded: set the map pin)'}`)
          : key === 'countersign' ? st.by
            : key === 'stripe' ? (st.existing ? `${st.accountId}, its existing account kept` : `${st.accountId}${st.prefilled ? ', prefilled' : ', NOT prefilled'}${st.prefillError ? ` (${st.prefillError})` : ''}${st.warnings && st.warnings.length ? `. Warnings: ${st.warnings.join('; ')}` : ''}`)
              : key === 'qor' ? (st.existing ? `${st.merchantId}, already boarded` : `${st.merchantId}${st.documents ? `. ${st.documents}` : ''}`)
                : key === 'email' ? `Sent to ${st.to}`
                  : key === 'agentCommission' ? `$${Number(st.amount || 0).toFixed(2)} owed to ${agentName || 'the agent'}`
                    : key === 'agentOrders' ? `${st.rate}% of every online order’s subtotal, from approval on` : ''
    return (
      <Box key={key} sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', py: 0.75 }}>
        <Box sx={{ pt: '1px', width: 20, display: 'flex', justifyContent: 'center' }}>{icon}</Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 14, color: T.text }}>{label}</Typography>
          {detail && <Typography sx={{ fontSize: 13, color: st && !st.ok ? T.error : T.hint, overflowWrap: 'anywhere' }}>{detail}</Typography>}
        </Box>
      </Box>
    )
  })
}

/**
 * A merchant already on RushMyFood (backend utils/currentMerchants.js):
 * approval attaches the application to their restaurant instead of creating
 * one. Shows which restaurant and why, what approval will do, and lets a
 * super admin create a new one instead or link a different restaurant.
 */
function ExistingRestaurantCard({ id, existing: e, decision, status, run }) {
  const [setRestaurant, { loading }] = useMutation(SET_RESTAURANT)
  const [picking, setPicking] = useState(false)
  const [choice, setChoice] = useState('')
  const { data: choices } = useQuery(RESTAURANT_CHOICES, { skip: !picking, fetchPolicy: 'cache-first' })
  const open = ['DRAFT', 'SIGNED'].includes(status)
  if (!e && !(open && decision === 'NEW')) return null
  const link = (restaurantId, createNew, message) => run(() => setRestaurant({ variables: { id, restaurantId: restaurantId || null, createNew: !!createNew } }), message)

  if (e && e.how === 'ATTACHED') {
    return (
      <Card>
        <CardTitle action={<Badge kind="green">Existing restaurant</Badge>}>Attached to {e.name}</CardTitle>
        <Typography sx={{ fontSize: 14, color: T.text2 }}>
          Approved onto restaurant #{e.restaurantId}{e.address ? `, ${e.address}` : ''}. Its listing, menu and owner login stayed as they were.
        </Typography>
      </Card>
    )
  }

  return (
    <Card sx={e ? { borderColor: T.infoBorder } : undefined}>
      {e ? (
        <>
          <CardTitle action={<Badge kind="info">{e.how === 'LINKED' ? 'Linked' : 'Same name and address'}</Badge>}>Already on RushMyFood</CardTitle>
          <KV columns={1} rows={[
            ['Restaurant', `${e.name} (#${e.restaurantId})`],
            ['Address', e.address],
            ['Owner login', [e.ownerName, e.ownerEmail].filter(Boolean).join(' · ') || 'None'],
          ]} />
          <Typography sx={{ fontSize: 14, color: T.text2, mt: 1.5, lineHeight: 1.6 }}>
            Approving attaches this application to it: no new restaurant or login. The restaurant keeps its listing and menu, takes the commission in this
            agreement, {e.hasStripe ? 'keeps its Stripe account' : 'gets a Stripe account'} and {e.hasQor ? 'stays boarded with QorCommerce' : 'is boarded with QorCommerce'}.
          </Typography>
        </>
      ) : (
        <>
          <CardTitle>New restaurant</CardTitle>
          <Typography sx={{ fontSize: 14, color: T.text2 }}>You chose to create a new restaurant for this application, even though one matched.</Typography>
        </>
      )}
      {open && (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1.5, ml: -1 }}>
          {e ? (
            <LinkButton disabled={loading} onClick={() => link(null, true, 'Approving will create a new restaurant for this application.')}>Create a new restaurant instead</LinkButton>
          ) : (
            <LinkButton disabled={loading} onClick={() => link(null, false, 'Looking for an existing restaurant with the same name and address again.')}>Look for an existing restaurant again</LinkButton>
          )}
          <LinkButton onClick={() => setPicking(x => !x)}>{picking ? 'Cancel' : 'Link a different restaurant'}</LinkButton>
        </Box>
      )}
      {open && picking && (
        <Box sx={{ mt: 1.5 }}>
          <FieldLabel htmlFor="link-restaurant" label="Restaurant" />
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <TextField id="link-restaurant" select size="small" value={choice} onChange={ev => setChoice(ev.target.value)} sx={{ flex: 1, minWidth: 200 }}>
              {((choices && choices.currentMerchants) || []).map(r => (
                <MenuItem key={r.restaurantId} value={r.restaurantId}>{r.name}{r.address ? ` — ${r.address}` : ''}</MenuItem>
              ))}
            </TextField>
            <PrimaryButton
              busy={loading} disabled={!choice}
              onClick={async () => { await link(choice, false, 'Linked: approving attaches this application to that restaurant.'); setPicking(false) }}
            >
              Link
            </PrimaryButton>
          </Box>
        </Box>
      )}
    </Card>
  )
}

/**
 * id: the application. version: bumped when the form beside it saves, to
 * reload. onFeesChanged: the fees changed here, so the form reloads.
 */
export function useApplicationReview(id, { skip = false } = {}) {
  return useQuery(GET_APPLICATION, { variables: { id }, skip, fetchPolicy: 'network-only' })
}

/** query: useApplicationReview(id)'s result, started by the page as it opens. */
export default function ApplicationReview({ id, query, version, onChanged, onFeesChanged }) {
  const { data, loading, error, refetch } = query
  const [notice, setNotice] = useState(null)
  const [actionError, setActionError] = useState(null)
  const [approve, { loading: approving }] = useMutation(APPROVE)
  const [resendStripe, { loading: resendingStripe }] = useMutation(RESEND_STRIPE)
  const [resendLink, { loading: resendingLink }] = useMutation(RESEND_LINK)

  // The form saved: reload what it changes here (missing items, documents).
  useEffect(() => {
    if (version) refetch().catch(() => {})
  }, [version, refetch])

  if (loading && !data) return <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress sx={{ color: T.red }} /></Box>
  if (error) return <Notice severity="error">{error.message}</Notice>
  const r = data && data.partnerSignupApplication
  if (!r) return null
  const s = r.signup
  const app = parse(r.application, null)
  const consents = parse(r.consents, [])
  const missing = parse(r.missing, [])
  const log = parse(s.onboardingLog, {})
  const owners = (app && app.owners) || []

  const run = async (fn, message, { fees } = {}) => {
    setNotice(null)
    setActionError(null)
    try {
      await fn()
      setNotice(message)
      await refetch()
      if (onChanged) onChanged()
      if (fees && onFeesChanged) onFeesChanged()
    } catch (e) {
      setActionError(e.message)
    }
  }
  const download = (doc) => downloadDocument(doc).catch((e) => setActionError(e.message))

  const docsByKind = (kinds) => r.documents.filter((d) => kinds.includes(d.kind))
  const kycDocs = r.documents.filter((d) => !['SIGNED_AGREEMENT', 'EXECUTED_AGREEMENT', 'MENU'].includes(d.kind))
  const menuDocs = docsByKind(['MENU'])
  const agreements = docsByKind(['SIGNED_AGREEMENT', 'EXECUTED_AGREEMENT'])

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 2 }}>
      {notice && <Notice severity="success" sx={{ mb: 0 }} onClose={() => setNotice(null)}>{notice}</Notice>}
      {actionError && <Notice severity="error" sx={{ mb: 0 }} onClose={() => setActionError(null)}>{actionError}</Notice>}

      <ExistingRestaurantCard id={id} existing={r.existingRestaurant} decision={r.attachDecision} status={s.status} run={run} />

      {/* What happens next */}
      {s.status === 'DRAFT' && (
        <Card>
          <CardTitle sub="The applicant has not signed yet. Fill in the form here, or email them a link.">In progress</CardTitle>
          {missing.length > 0 ? (
            <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mb: 1 }}>
              {missing.map((m) => (
                <Box component="li" key={m.message} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', py: 0.5 }}>
                  <Box sx={{ color: T.redText, display: 'flex', mt: '2px' }}><Icon.Alert size={16} /></Box>
                  <Typography sx={{ fontSize: 14, color: T.text }}>{m.message}</Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography sx={{ fontSize: 14, color: T.green, mb: 1 }}>Everything is filled in; waiting for the owner to sign.</Typography>
          )}
          <LinkButton disabled={resendingLink} onClick={() => run(() => resendLink({ variables: { id } }), 'A new application link was emailed to the applicant.')} sx={{ ml: -1 }}>
            <Icon.Mail size={16} />
            Email the applicant a link (to finish or add documents)
          </LinkButton>
        </Card>
      )}

      {['SIGNED', 'ONBOARDED'].includes(s.status) && (
        <Card sx={s.status === 'SIGNED' ? { borderColor: '#E6B8AD' } : undefined}>
          <CardTitle
            sub={s.status !== 'SIGNED' ? null
              : r.existingRestaurant
                ? `Attaches it to ${r.existingRestaurant.name}: countersigns the agreement, sets the signed commission, ${r.existingRestaurant.hasStripe ? 'keeps its Stripe account' : 'creates its Stripe account'}, ${r.existingRestaurant.hasQor ? 'keeps its QorCommerce boarding' : 'submits QorCommerce boarding'}, and emails the owner. No new restaurant or login. Each step can be retried.`
                : 'Creates the owner’s dashboard login and the restaurant (hidden from customers), countersigns the agreement, creates the Stripe account with every detail prefilled, submits QorCommerce boarding, and emails the owner. Each step can be retried.'}
          >
            Approve &amp; onboard
          </CardTitle>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
            <PrimaryButton
              tone="green" busy={approving}
              onClick={() => run(() => approve({ variables: { id } }), 'Onboarding ran. Check each step below.')}
            >
              {s.status === 'SIGNED' ? <><Icon.Check size={16} />Approve &amp; onboard</> : 'Retry failed steps'}
            </PrimaryButton>
            {s.status === 'ONBOARDED' && log.stripe && log.stripe.ok && (
              <OutlineButton disabled={resendingStripe} onClick={() => run(() => resendStripe({ variables: { id } }), 'A new Stripe setup link was emailed to the owner.')}>
                Send Stripe link again
              </OutlineButton>
            )}
          </Box>
          <OnboardSteps log={log} agentName={s.agentName} />
        </Card>
      )}

      {s.status === 'ONBOARDED' && s.agentName && <AgentEarningsCard id={id} version={version} />}

      {r.pricing && <PricingSection id={id} pricing={r.pricing} signupStatus={s.status} run={run} />}

      {/* Agreement */}
      <Card>
        <CardTitle>Agreement</CardTitle>
        <KV columns={1} rows={[
          ['Version', s.agreementVersion],
          ['Signed', s.signedAt ? `${fmtDate(s.signedAt)} by ${s.signerName}, ${s.signerTitle}` : 'Not yet'],
          ['Signer email', s.signerEmail],
          ['Signer IP', s.signerIp],
          ['Countersigned', s.countersignedAt ? `${fmtDate(s.countersignedAt)} by ${s.countersignerName}, ${s.countersignerTitle}` : '—'],
          // The Order Percentage signed in Schedule A; approval sets it as the restaurant's commission.
          ['Commission signed', app && app.fees
            ? `Delivery ${app.fees.deliveryCommissionRate}% · Self-delivery ${app.fees.selfDeliveryCommissionRate}% · Pickup ${app.fees.pickupCommissionRate}%`
            : s.signedAt ? '15% of subtotal (signed before the split)' : '—'],
        ]} />
        {agreements.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            {agreements.map((d) => <FileRow key={d._id} title={KIND_LABELS[d.kind]} name={d.name} onDownload={() => download(d)} />)}
          </Box>
        )}
      </Card>

      {/* Documents */}
      <Card>
        <CardTitle sub="QorCommerce: upload these in the Qor portal and tag each to its requirement. Stripe: owner IDs, the voided check and the EIN letter are attached to the connected account on approval.">
          KYB documents ({kycDocs.length})
        </CardTitle>
        {kycDocs.length === 0 ? <Typography sx={{ color: T.hint, fontSize: 14 }}>No documents yet.</Typography> : (
          <Box>
            {kycDocs.map((d) => (
              <FileRow
                key={d._id}
                title={`${KIND_LABELS[d.kind] || d.kind}${d.ownerIndex != null && owners[d.ownerIndex] ? `: ${owners[d.ownerIndex].first} ${owners[d.ownerIndex].last}` : ''}`}
                name={d.name}
                size={d.size}
                onDownload={() => download(d)}
              />
            ))}
          </Box>
        )}
        {s.status !== 'REJECTED' && s.status !== 'DRAFT' && (
          <LinkButton disabled={resendingLink} onClick={() => run(() => resendLink({ variables: { id } }), 'A new application link was emailed to the applicant.')} sx={{ mt: 1, ml: -1 }}>
            <Icon.Mail size={16} />
            Email the applicant a link (to finish or add documents)
          </LinkButton>
        )}
      </Card>

      <MenuSection id={id} docs={menuDocs} restaurantId={s.restaurantId} log={log} run={run} download={download} />

      {app && app.processing && (
        <Card>
          <CardTitle sub="Set from Signup Standards when the application was made.">Processing profile</CardTitle>
          <KV columns={1} rows={[
            ['Mix', `In person ${app.processing.mixInPerson}% · Mobile ${app.processing.mixMobile}% · eCommerce ${app.processing.mixEcommerce}% · MOTO ${app.processing.mixMoto}%`],
            ['Cards', Object.entries(app.processing.cards || {}).filter(([, v]) => v).map(([k]) => k).join(', ')],
            ['Monthly sales', app.processing.monthlySales && `$${app.processing.monthlySales}`],
            ['Highest / average ticket', `$${app.processing.highTicket || '—'} / $${app.processing.avgTicket || '—'}`],
          ]} />
        </Card>
      )}

      {consents.length > 0 && (
        <Card>
          <CardTitle>Authorizations</CardTitle>
          {consents.map((c) => (
            <Box key={c.key} sx={{ display: 'flex', gap: 1, alignItems: 'center', py: 0.4 }}>
              <Box sx={{ color: c.accepted ? T.green : T.inputHover, display: 'flex' }}>{c.accepted ? <Icon.Check size={16} /> : <Icon.Circle size={16} />}</Box>
              <Typography sx={{ fontSize: 14, color: c.accepted ? T.text : T.hint }}>{c.title}</Typography>
            </Box>
          ))}
        </Card>
      )}
    </Box>
  )
}
